import { useState, useEffect, useCallback } from 'react';
import { UserRole, AdminPermission } from '../types/userTypes';
import roleService from '../services/roleService';
import { useAuth } from '../contexts/AuthContext';

interface UseRolesReturn {
  currentUserRole: UserRole;
  currentUserPermissions: AdminPermission[];
  customPermissions?: AdminPermission[];
  isLoading: boolean;
  error: string | null;
  hasPermission: (permission: AdminPermission) => boolean;
  isAdmin: boolean;
  canManageUsers: boolean;
  refreshRoles: () => Promise<void>;
}

export const useRoles = (): UseRolesReturn => {
  const { currentUser } = useAuth();
  const [currentUserRole, setCurrentUserRole] = useState<UserRole>('user');
  const [currentUserPermissions, setCurrentUserPermissions] = useState<AdminPermission[]>([]);
  const [customPermissions, setCustomPermissions] = useState<AdminPermission[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshRoles = useCallback(async () => {
    if (!currentUser) {
      setCurrentUserRole('user');
      setCurrentUserPermissions([]);
      setCustomPermissions(undefined);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const roleData = await roleService.getUserRole(currentUser.uid);
      setCurrentUserRole(roleData.role);
      setCurrentUserPermissions(roleData.permissions);
      setCustomPermissions(roleData.customPermissions);
    } catch (err) {
      console.error('Error fetching user roles:', err);
      setError('Failed to load user permissions');
      setCurrentUserRole('user');
      setCurrentUserPermissions([]);
      setCustomPermissions(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    refreshRoles();
  }, [refreshRoles]);

  const hasPermission = useCallback((permission: AdminPermission): boolean => {
    return currentUserPermissions.includes(permission);
  }, [currentUserPermissions]);

  const isAdmin = currentUserRole !== 'user';
  const canManageUsers = hasPermission('user_management');

  return {
    currentUserRole,
    currentUserPermissions,
    customPermissions,
    isLoading,
    error,
    hasPermission,
    isAdmin,
    canManageUsers,
    refreshRoles,
  };
}; 